export const NOME_EVENTO_CLIQUE_BANNER_GESTOES = 'clicou no banner entre gestão produção e beta'

export type AmbienteGestao = 'gestao_producao' | 'gestao_beta'

export type PropriedadesCliqueBannerGestoes = Record<
  'origem' | 'destino',
  AmbienteGestao
>

interface SurveyPosthog {
  id: string
  type: string
  conditions?: {
    events?: {
      values?: Array<{ name: string }>
    } | null
  } | null
}

interface EventoCapturadoPosthog {
  event?: string
  properties?: Record<string, unknown>
}

interface ContextoCarregamentoSurveys {
  isLoaded: boolean
}

export interface ClienteSurveyPosthog {
  onSurveysLoaded: (
    callback: (surveys: SurveyPosthog[], contexto?: ContextoCarregamentoSurveys) => void,
  ) => () => void
  canRenderSurvey: (idSurvey: string) => { visible: boolean } | null
  displaySurvey: (
    idSurvey: string,
    opcoes: {
      displayType: 'popover'
      position: 'middle_center'
      ignoreConditions: boolean
      ignoreDelay: boolean
      properties: PropriedadesCliqueBannerGestoes
    },
  ) => void
  on: (
    evento: 'eventCaptured',
    callback: (eventoCapturado: EventoCapturadoPosthog) => void,
  ) => () => void
}

const surveyEhAcionadaPeloClique = (survey: SurveyPosthog): boolean => {
  if (survey.type !== 'api') return false

  return survey.conditions?.events?.values?.some(
    ({ name }) => name === NOME_EVENTO_CLIQUE_BANNER_GESTOES,
  ) === true
}

export const criarFluxoSurveyBannerGestoes = (
  cliente: ClienteSurveyPosthog,
  posthogEstaAtivo: () => boolean,
  aoInicializarPosthog: (callback: () => void) => () => void,
) => {
  let surveysCarregadas = false
  let surveysAcionadasPeloClique: SurveyPosthog[] = []
  let limparPreparacaoAtual: (() => void) | null = null
  let removerListenerCicloDeVida: (() => void) | null = null
  let idSurveyEmExibicao: string | null = null
  let aoFinalizarSurvey: (() => void) | null = null

  const cancelarInteracao = () => {
    removerListenerCicloDeVida?.()
    removerListenerCicloDeVida = null
    idSurveyEmExibicao = null
    aoFinalizarSurvey = null
  }

  const finalizarSurvey = () => {
    const callback = aoFinalizarSurvey
    cancelarInteracao()
    callback?.()
  }

  const preparar = (): (() => void) => {
    limparPreparacaoAtual?.()
    surveysCarregadas = false
    surveysAcionadasPeloClique = []

    let preparacaoAtiva = true
    let removerEsperaInicializacao: (() => void) | null = null
    let removerListenerCarregamento: (() => void) | null = null

    const iniciarCarregamentoSurveys = () => {
      if (!preparacaoAtiva || removerListenerCarregamento) return

      removerEsperaInicializacao?.()
      removerEsperaInicializacao = null
      removerListenerCarregamento = cliente.onSurveysLoaded((surveys, contexto) => {
        surveysCarregadas = contexto?.isLoaded !== false

        if (!surveysCarregadas) {
          surveysAcionadasPeloClique = []
          return
        }

        surveysAcionadasPeloClique = surveys.filter(surveyEhAcionadaPeloClique)
      })
    }

    const limparPreparacao = () => {
      if (!preparacaoAtiva) return

      preparacaoAtiva = false
      removerEsperaInicializacao?.()
      removerListenerCarregamento?.()
      surveysCarregadas = false
      surveysAcionadasPeloClique = []
      cancelarInteracao()

      if (limparPreparacaoAtual === limparPreparacao) {
        limparPreparacaoAtual = null
      }
    }

    limparPreparacaoAtual = limparPreparacao

    if (posthogEstaAtivo()) {
      iniciarCarregamentoSurveys()
    } else {
      removerEsperaInicializacao = aoInicializarPosthog(iniciarCarregamentoSurveys)
    }

    return limparPreparacao
  }

  const exibirSurvey = (
    propriedades: PropriedadesCliqueBannerGestoes,
    aoFinalizar: () => void,
  ): boolean => {
    if (removerListenerCicloDeVida) return true
    if (!posthogEstaAtivo() || !surveysCarregadas) return false

    const surveysElegiveis = surveysAcionadasPeloClique.filter(
      ({ id }) => cliente.canRenderSurvey(id)?.visible === true,
    )
    if (surveysElegiveis.length !== 1) return false

    const surveyElegivel = surveysElegiveis[0]
    idSurveyEmExibicao = surveyElegivel.id
    aoFinalizarSurvey = aoFinalizar
    removerListenerCicloDeVida = cliente.on('eventCaptured', (eventoCapturado) => {
      const eventoEhDaSurveyExibida =
        eventoCapturado.properties?.$survey_id === idSurveyEmExibicao

      if (!eventoEhDaSurveyExibida) return

      const surveyFoiFechada = eventoCapturado.event === 'survey dismissed'
      const surveyFoiConcluida =
        eventoCapturado.event === 'survey sent' &&
        eventoCapturado.properties?.$survey_completed === true

      if (surveyFoiFechada || surveyFoiConcluida) finalizarSurvey()
    })

    try {
      cliente.displaySurvey(surveyElegivel.id, {
        displayType: 'popover',
        position: 'middle_center',
        ignoreConditions: false,
        ignoreDelay: true,
        properties: propriedades,
      })
      return true
    } catch (erro) {
      cancelarInteracao()
      throw erro
    }
  }

  return {
    preparar,
    exibirSurvey,
    cancelarInteracao,
  }
}
